import React, { useRef } from 'react';
import DOMPurify from 'dompurify';
import { Button, Descriptions, Space } from 'antd';
import { routeHistory } from '@nackle/biw-web-router';
import { EditOutlined } from '@ant-design/icons';
import { Routes } from '../../../routes';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteNotification from '../../gql/hooks/useDeleteNotification'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags'; 
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { DEFAULT_LOCALE_CODE } from '../../../const';
let expandedRows = [];

const NotificationsListPage = () => { 
    const listPageRef = useRef( null );

    const { deleteNotification, isDeleting } = useDeleteNotification();
    const [ showBrands ] = useFeatureFlags( [ flags.BRANDS ] );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Notifications' }
    ];

    const uiStrings = {
        cardTitle: 'Notifications',
        createButtonTitle: 'Create New Notification'
    };

    const handleDeleteClick = async ( id ) => {
        await deleteNotification( id, () => {
            listPageRef.current.reload();
        } );
    };

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateNotification.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };

    const renderExpandedRow = ( item ) => {

        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <Descriptions bordered size='medium' layout='vertical' items={ [
                            {
                                key: 'desc',
                                span: 'filled',
                                label: `Content (${ DEFAULT_LOCALE_CODE })`,
                                children: item.content?.length > 0 ?
                                    <span dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize( item.content ) } } /> :
                                    <span className='canceled'>No content</span>
                            }, {
                                key: 'publishat',
                                label: 'Publish Date',
                                children: item.publishAt ? <span>{ item.publishAt }</span> : <span className='canceled'>Not set</span>
                            }, {
                                key: 'unpublishAt',
                                label: 'Unpublish Date',
                                children: item.unpublishAt ? <span>{ item.unpublishAt }</span> : <span className='canceled'>Not set</span>
                            }, {
                                key: 'unfeatureat',
                                label: 'Unfeature Date',
                                children: item.unfeatureAt ? <span>{ item.unfeatureAt }</span> : <span className='canceled'>Not set</span>
                            }
                        ] }/>
                    </div>
                </div>

            </div>
        );
    };

    const tableColumns = [
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            width: 350,
            fixed: 'left',
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No translation for this language</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span> 
                    );
                }
            }
        },
        {
            dataIndex: 'weight',
            key: 'weight',
            title: 'Weight',
            sorter: ( a, b ) => 0,
            sortKey: 'weight',
            render: ( weight, i ) => i.isSticky && <span>{ weight }</span>
        },
        ...( showBrands ?
            [
                {
                    dataIndex: 'brandIds',
                    key: 'brands',
                    title: 'Brand',
                    sortKey: 'brands',
                    render: ( b ) => {
                        return b.map( i => {
                            const brand = listPageRef.current.getBrand( i );
                            return brand ? <ColoredTag key={ brand.id } value={ brand.id }>{ brand.name }</ColoredTag> : null ;
                        } );
                    }
                },
            ] : [] ),
        {
            dataIndex: 'isPublished',
            key: 'isPublished',
            title: 'Published',
            render: ( isPublished ) => <ColoredTag.Publish isPublished={ isPublished } />
        },
        {
            dataIndex: 'isSticky',
            key: 'isSticky',
            title: 'Sticky',
            render: ( isSticky ) => <ColoredTag.Sticky isSticky={ isSticky } />
        },
        {
            dataIndex: 'createdAt',
            key: 'createdAt',
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => routeHistory.push( Routes.EditNotification.buildPath( item.id ) ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the notification <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            }   
        }
    ];   
  
    return (
        <SearchQueryList 
            ref={ listPageRef }
            type="notifications" 
            uiStrings={ uiStrings }
            breadcrumbs={ breadcrumbs }
            tableColumns={ tableColumns }
            isDeleting={ isDeleting }
            renderExpandedRowCallback= { renderExpandedRow }
            createClickCallback={ handleCreateClick }
            setExpandedRowsCallback= { handleSetExpandedRows }
            defaultExpandedRows= { expandedRows }
        />
    );
};


export default NotificationsListPage;
